import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FaFileInvoiceDollar,
  FaCalendarAlt,
  FaBuilding,
  FaSearch,
  FaFilter,
  FaDownload,
  FaEdit,
  FaEye,
  FaPlus,
  FaPrint,
  FaExclamationTriangle,
  FaCheckCircle,
  FaBars,
  FaUser,
  FaBell,
  FaCog,
  FaSignOutAlt,
  FaHome,
  FaChartBar,
  FaUsers,
  FaFileAlt,
  FaTasks,
  FaWarehouse,
} from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/dashboardDetails";

const toNum = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

const ProjectOrderDashboard = () => {
  const [isDark, setIsDark] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // localStorage helper
  const getLS = (key) => localStorage.getItem(key) || "";

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS = getLS("userId") || getLS("loginuserid");

  // common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdLS,
      "x-branchtype": branchTypeLS,
      "x-userid": userIdLS,
    }),
    [finYearId, finYear, branchIdLS, branchTypeLS, userIdLS]
  );

  // API data state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);

  const location = useLocation();

  // contractId from URL (support both contractid + contractId)
  const contractId = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    const cid = qs.get("contractid") || qs.get("contractId") || "";
    const n = Number(cid || 0);
    return Number.isFinite(n) ? n : 0;
  }, [location.search]);

  const withContractId = (path) => {
    if (!contractId) return path;

    const url = new URL(path, window.location.origin);
    url.searchParams.set("contractId", contractId);
    return url.pathname + url.search;
  };

  const sidebarItems = useMemo(
    () => [
      { name: "Date/LOA", icon: FaCalendarAlt, path: withContractId("/OrderManagement/ProjectEntry") },
      { name: "BOQ/Operative", icon: FaFileInvoiceDollar, path: withContractId("/OrderManagement/BOQ"), active: true },
      { name: "Program/DPR", icon: FaChartBar, path: withContractId("/OrderManagement/DailyProgressMonthlyWise") },
      { name: "Purchase Order", icon: FaFileAlt, path: withContractId("/OrderManagement/PurchaseOrderListing") },
      { name: "Rate Approval", icon: FaCheckCircle, path: withContractId("/OrderManagement/RateApprove") },
      { name: "Risk/Issues", icon: FaExclamationTriangle, path: withContractId("/OrderManagement/RiskAndIssue") },
      { name: "Documentation", icon: FaFileAlt, path: withContractId("/OrderManagement/ProjectEntryDocumentation") },
      { name: "Photos", icon: FaBuilding, path: withContractId("/OrderManagement/PhotoDocumentation") },
      { name: "Reconciliation", icon: FaTasks, path: withContractId("/OrderManagement/Reconciliation") },
      { name: "Do/Don't's", icon: FaWarehouse, path: withContractId("/OrderManagement/Rules") },
      { name: "Vendor Details", icon: FaUsers, path: withContractId("/OrderManagement/ProjectVendorDetails") },
    ],
    [contractId]
  );

  // load BOQ rows
  useEffect(() => {
    const load = async () => {
      if (!contractId) {
        setError("contractId not found in URL. Example: /OrderManagement/BOQ?contractid=2034");
        setRows([]);
        return;
      }

      setError("");
      setLoading(true);

      try {
        const res = await axios.get(`${API_BASE}/boq`, {
          params: { contractId },
          headers: commonHeaders,
        });

        const list = res.data?.list || [];
        setRows(list);
      } catch (e) {
        setRows([]);
        setError(e?.response?.data?.message || e?.message || "Failed to load BOQ");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [contractId, commonHeaders]);

  // map SP row -> UI row
  const projectData = useMemo(() => {
    return (rows || []).map((r, idx) => ({
      sNo: idx + 1,
      projectcategoryid: r.fld_projectcategoryid,
      workId: r.fld_WorkId,
      categoryName: r.fld_Projectcategoryname || "",
      projectDetails: r.fld_work || "",
      unit: r.fld_unit || "",
      boqRate: toNum(r.fld_rateperunit),
      boqQty: toNum(r.fld_qty),
      boqAmount: toNum(r.amount),
      opDate: String(r.fld_oprateperunit ?? ""),
      opBoq: toNum(r.fld_opQty),
      opAmount: toNum(r.fld_opamount),
      lastEditDate: `${r.fld_user ?? ""} ${r.fld_LEDate ?? ""}`.trim(),
    }));
  }, [rows]);

  const categories = useMemo(() => {
    const set = new Set();
    projectData.forEach((x) => {
      const c = String(x.categoryName || "").trim();
      if (c) set.add(c);
    });
    return Array.from(set);
  }, [projectData]);

  const filteredData = useMemo(() => {
    return projectData.filter((item) => {
      const matchesSearch =
        item.projectDetails.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.categoryName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory =
        selectedCategory === "all" || item.categoryName === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [projectData, searchTerm, selectedCategory]);

  const totalBOQAmount = useMemo(
    () => projectData.reduce((sum, item) => sum + toNum(item.boqAmount), 0),
    [projectData]
  );

  const totalOPAmount = useMemo(
    () => projectData.reduce((sum, item) => sum + toNum(item.opAmount), 0),
    [projectData]
  );

  const onPrint = () => {
    const url = `/OrderManagement/BOQPrint?contractid=${contractId}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className={`min-h-screen overflow-x-hidden transition-all duration-300 ${
        isDark ? "bg-gray-900" : "bg-gray-100"
      }`}
    >
      {/* Top Navigation Bar */}
      <div
        className={`sticky top-0 z-50 backdrop-blur-md border-b ${
          isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        } shadow-lg`}
      >
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark
                    ? "hover:bg-gray-700 text-gray-300"
                    : "hover:bg-gray-100 text-gray-600"
                }`}
              >
                <FaBars size={20} />
              </button>
              <div className="flex items-center gap-3">
                <FaHome className="text-blue-600 text-xl" />
                <h1 className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Project Management System
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button
                className={`p-2 rounded-lg transition-colors relative ${
                  isDark
                    ? "hover:bg-gray-700 text-gray-300"
                    : "hover:bg-gray-100 text-gray-600"
                }`}
              >
                <FaBell size={18} />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  3
                </span>
              </button>

              <button
                className={`p-2 rounded-lg transition-colors ${
                  isDark
                    ? "hover:bg-gray-700 text-gray-300"
                    : "hover:bg-gray-100 text-gray-600"
                }`}
              >
                <FaCog size={18} />
              </button>

              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                  <FaUser className="text-white text-sm" />
                </div>
                <span className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Admin
                </span>
              </div>

              <button
                className={`p-2 rounded-lg transition-colors ${
                  isDark
                    ? "hover:bg-gray-700 text-gray-300"
                    : "hover:bg-gray-100 text-gray-600"
                }`}
              >
                <FaSignOutAlt size={18} />
              </button>

              <button
                onClick={() => setIsDark(!isDark)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark
                    ? "bg-yellow-600 hover:bg-yellow-700 text-white"
                    : "bg-gray-800 hover:bg-gray-900 text-white"
                }`}
              >
                {isDark ? "☀️" : "🌙"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex overflow-x-hidden">
        {/* Sidebar */}
        <div
          className={`${sidebarOpen ? "w-64" : "w-16"} flex-shrink-0 transition-all duration-300 ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          } border-r min-h-screen`}
        >
          <div className="p-4">
            {sidebarItems.map((item, index) => (
              <Link
                to={item.path}
                key={index}
                className={`flex items-center gap-3 p-3 rounded-lg mb-2 cursor-pointer transition-all duration-200 ${
                  window.location.pathname === item.path
                    ? isDark
                      ? "bg-blue-600 text-white"
                      : "bg-blue-500 text-white"
                    : isDark
                    ? "hover:bg-gray-700 text-gray-300"
                    : "hover:bg-gray-100 text-gray-700"
                }`}
              >
                <item.icon size={18} />
                {sidebarOpen && <span className="text-sm font-medium">{item.name}</span>}
              </Link>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0 overflow-x-hidden">
          <div
            className={`transition-all duration-300 ${
              isDark
                ? "bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900"
                : "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"
            }`}
          >
            {/* Header */}
            <div
              className={`backdrop-blur-md border-b ${
                isDark ? "bg-gray-900/80 border-gray-700" : "bg-white/80 border-gray-200"
              }`}
            >
              <div className="px-3 md:px-6 py-4">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
                  <div>
                    <h1
                      className={`text-2xl lg:text-3xl font-bold flex items-center gap-3 ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      <FaFileInvoiceDollar className="text-blue-500" />
                      BOQ/OPERATIVE
                    </h1>
                    <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Contract ID: <span className="font-semibold">{contractId || "-"}</span>
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={onPrint}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105"
                    >
                      <FaPrint />
                      Print
                    </button>
                  </div>
                </div>

                {loading && (
                  <div className={`mt-3 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Loading BOQ...
                  </div>
                )}
                {!!error && <div className="mt-3 text-sm text-red-500">{error}</div>}
              </div>
            </div>

            <div className="p-3 md:p-6 space-y-6 overflow-x-hidden">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                <div
                  className={`p-6 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                    isDark
                      ? "bg-gradient-to-br from-blue-900/50 to-blue-800/30 backdrop-blur-md border-blue-700 text-white"
                      : "bg-gradient-to-br from-blue-500 to-blue-600 text-white border-blue-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm font-medium">Total Rows</p>
                      <p className="text-3xl font-bold">{projectData.length}</p>
                    </div>
                    <FaBuilding className="text-4xl text-blue-200" />
                  </div>
                </div>

                <div
                  className={`p-6 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                    isDark
                      ? "bg-gradient-to-br from-green-900/50 to-green-800/30 backdrop-blur-md border-green-700 text-white"
                      : "bg-gradient-to-br from-green-500 to-green-600 text-white border-green-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm font-medium">BOQ Amount</p>
                      <p className="text-2xl font-bold">₹{totalBOQAmount.toLocaleString("en-IN")}</p>
                    </div>
                    <FaFileInvoiceDollar className="text-4xl text-green-200" />
                  </div>
                </div>

                <div
                  className={`p-6 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                    isDark
                      ? "bg-gradient-to-br from-purple-900/50 to-purple-800/30 backdrop-blur-md border-purple-700 text-white"
                      : "bg-gradient-to-br from-purple-500 to-purple-600 text-white border-purple-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm font-medium">OP Amount</p>
                      <p className="text-2xl font-bold">₹{totalOPAmount.toLocaleString("en-IN")}</p>
                    </div>
                    <FaCalendarAlt className="text-4xl text-purple-200" />
                  </div>
                </div>

                <div
                  className={`p-6 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                    isDark
                      ? "bg-gradient-to-br from-orange-900/50 to-orange-800/30 backdrop-blur-md border-orange-700 text-white"
                      : "bg-gradient-to-br from-orange-500 to-orange-600 text-white border-orange-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-100 text-sm font-medium">Categories</p>
                      <p className="text-3xl font-bold">{categories.length}</p>
                    </div>
                    <FaFilter className="text-4xl text-orange-200" />
                  </div>
                </div>
              </div>

              {/* Filters and Search */}
              <div
                className={`p-4 md:p-6 rounded-2xl shadow-xl border ${
                  isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
                }`}
              >
                <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                  <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center w-full lg:w-auto">
                    <div className="relative w-full sm:w-72">
                      <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                      <input
                        type="text"
                        placeholder="Search project details..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`w-full pl-10 pr-4 py-3 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                    </div>

                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className={`w-full sm:w-60 px-4 py-3 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="all">All Categories</option>
                      {categories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="text-sm text-gray-500">
                    Showing {filteredData.length} of {projectData.length} records
                  </div>
                </div>
              </div>

              {/* Main Data Table */}
              <div
                className={`rounded-2xl shadow-2xl border overflow-hidden ${
                  isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
                }`}
              >
                <div
                  className={`px-4 md:px-6 py-4 border-b ${
                    isDark ? "bg-blue-900/30 border-gray-700" : "bg-blue-600 text-white border-blue-700"
                  }`}
                >
                  <h2 className="text-xl font-bold flex items-center gap-3">
                    <FaFileInvoiceDollar className="text-2xl" />
                    BOQ / OPERATIVE
                  </h2>
                </div>

                <div className="w-full overflow-hidden">
                  <table className="w-full table-fixed">
                    <thead className={`${isDark ? "bg-blue-700 text-white" : "bg-blue-600 text-white"}`}>
                      <tr>
                        <th className="w-12 px-2 md:px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider">
                          S.No
                        </th>
                        <th className="w-40 px-2 md:px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-2 md:px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider">
                          Work Details
                        </th>
                        <th className="w-20 px-2 md:px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider">
                          Unit
                        </th>
                        <th className="w-24 px-2 md:px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider">
                          BOQ Rate
                        </th>
                        <th className="w-20 px-2 md:px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider">
                          BOQ Qty
                        </th>
                        <th className="w-28 px-2 md:px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider">
                          BOQ Amt
                        </th>
                        <th className="hidden lg:table-cell w-24 px-2 md:px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider">
                          OP Rate
                        </th>
                        <th className="hidden lg:table-cell w-20 px-2 md:px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider">
                          OP BOQ
                        </th>
                        <th className="hidden lg:table-cell w-28 px-2 md:px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider">
                          OP Amt
                        </th>
                        <th className="hidden xl:table-cell w-32 px-2 md:px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider">
                          Last Edit
                        </th>
                      </tr>
                    </thead>

                    <tbody
                      className={`${isDark ? "bg-gray-800" : "bg-white"} divide-y ${
                        isDark ? "divide-gray-700" : "divide-gray-200"
                      }`}
                    >
                      {!loading && filteredData.length === 0 ? (
                        <tr>
                          <td
                            colSpan={12}
                            className={`px-4 py-10 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}
                          >
                            No Record Found
                          </td>
                        </tr>
                      ) : (
                        filteredData.map((item, index) => (
                          <tr
                            key={index}
                            className={`${
                              isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                            } transition-colors duration-200`}
                          >
                            <td className={`px-2 md:px-4 py-3 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                              {item.sNo}
                            </td>

                            <td className={`px-2 md:px-4 py-3 text-sm ${isDark ? "text-blue-300" : "text-blue-600"}`}>
                              <span className="font-medium break-words whitespace-normal">
                                {item.categoryName}
                              </span>
                            </td>

                            <td className={`px-2 md:px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                              <p className="break-words whitespace-normal line-clamp-2 leading-relaxed">
                                {item.projectDetails}
                              </p>
                            </td>

                            <td className={`px-2 md:px-4 py-3 text-sm font-medium ${isDark ? "text-purple-300" : "text-purple-600"}`}>
                              {item.unit}
                            </td>

                            <td className="px-2 md:px-4 py-3 text-sm font-semibold text-red-500">
                              ₹{toNum(item.boqRate).toFixed(2)}
                            </td>
                            <td className="px-2 md:px-4 py-3 text-sm text-red-500">
                              {toNum(item.boqQty).toFixed(2)}
                            </td>
                            <td className="px-2 md:px-4 py-3 text-sm font-bold text-red-500">
                              ₹{toNum(item.boqAmount).toLocaleString("en-IN")}
                            </td>

                            <td className="hidden lg:table-cell px-2 md:px-4 py-3 text-sm text-blue-500">
                              {item.opDate}
                            </td>
                            <td className="hidden lg:table-cell px-2 md:px-4 py-3 text-sm text-blue-500">
                              {toNum(item.opBoq).toFixed(2)}
                            </td>
                            <td className="hidden lg:table-cell px-2 md:px-4 py-3 text-sm font-semibold text-blue-500">
                              ₹{toNum(item.opAmount).toFixed(4)}
                            </td>

                            <td className={`hidden xl:table-cell px-2 md:px-4 py-3 text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                              <div className="space-y-1">
                                {String(item.lastEditDate || "")
                                  .split(" ")
                                  .filter(Boolean)
                                  .map((part, i) => (
                                    <div key={i} className="whitespace-nowrap">
                                      {part}
                                    </div>
                                  ))}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Table Footer with Totals */}
                <div className={`px-4 md:px-6 py-4 border-t ${isDark ? "bg-gray-700/50 border-gray-600" : "bg-gray-50 border-gray-200"}`}>
                  <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2">
                    <div className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Total Records: {filteredData.length}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-6">
                      <div className={`text-sm font-semibold ${isDark ? "text-green-300" : "text-green-700"}`}>
                        Total BOQ Amount: ₹{totalBOQAmount.toLocaleString("en-IN")}
                      </div>
                      <div className={`text-sm font-semibold ${isDark ? "text-pink-300" : "text-pink-700"}`}>
                        Total OP Amount: ₹{totalOPAmount.toLocaleString("en-IN")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* end table */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectOrderDashboard;